export type LinearMention =
	| { kind: "issue"; href: string; identifier: string }
	| { kind: "project"; href: string; slugId: string; label: string | null }
	| { kind: "document"; href: string; slugId: string; label: string | null }
	| { kind: "user"; href: string; username: string }

export interface LinearMentionLookup {
	issues: string[]
	projects: string[]
	documents: string[]
}

type ParsedMentionUrl =
	| { kind: "issue"; identifier: string }
	| { kind: "project" | "document"; slugId: string }
	| { kind: "user"; username: string }

export const LINEAR_ISSUE_IDENTIFIER = /^[a-z][a-z0-9]*-[1-9]\d*$/i
// Linear appends a 12 character hex id to every project and document slug.
const SLUG_ID = /(?:^|-)([0-9a-f]{12})$/i
const USERNAME = /^[\w.-]+$/

export const LINEAR_MENTION_LOOKUP_LIMIT = 50

export function parseLinearMentionUrl(href: string): ParsedMentionUrl | null {
	let url: URL
	try {
		url = new URL(href)
	} catch {
		return null
	}
	if (url.protocol !== "https:" || url.hostname !== "linear.app") return null

	const [, kind, value] = url.pathname.split("/").filter(Boolean)
	if (!kind || !value) return null

	// A link to one comment inside an issue is a plain link in Linear too.
	if (kind === "issue" && !url.hash && LINEAR_ISSUE_IDENTIFIER.test(value)) {
		return { kind: "issue", identifier: value.toUpperCase() }
	}
	if (kind === "project" || kind === "document") {
		const slugId = SLUG_ID.exec(value)?.[1]
		return slugId ? { kind, slugId: slugId.toLowerCase() } : null
	}
	if (kind === "profiles" && !url.hash && USERNAME.test(value)) {
		return { kind: "user", username: value }
	}
	return null
}

// Linear writes a mention as a bare URL or as `[label](url)`. An issue link
// with any other text is a normal link in Linear, so it stays one here.
export function linearMentionFromLink({
	href,
	text,
}: {
	href: string
	text: string
}): LinearMention | null {
	const parsed = parseLinearMentionUrl(href)
	if (!parsed) return null

	const label = text.trim()
	const isBareUrl = label === "" || label === href.trim()

	switch (parsed.kind) {
		case "issue":
			if (!isBareUrl && label.toUpperCase() !== parsed.identifier) return null
			return { kind: "issue", href, identifier: parsed.identifier }
		case "user":
			if (!isBareUrl && label !== `@${parsed.username}`) return null
			return { kind: "user", href, username: parsed.username }
		default:
			return {
				kind: parsed.kind,
				href,
				slugId: parsed.slugId,
				label: isBareUrl ? null : label,
			}
	}
}

const uniqueSorted = (values: string[]) =>
	[...new Set(values)].sort().slice(0, LINEAR_MENTION_LOOKUP_LIMIT)

export function linearMentionLookup(
	mentions: readonly LinearMention[],
): LinearMentionLookup {
	const issues: string[] = []
	const projects: string[] = []
	const documents: string[] = []
	for (const mention of mentions) {
		if (mention.kind === "issue") issues.push(mention.identifier)
		if (mention.kind === "project") projects.push(mention.slugId)
		if (mention.kind === "document") documents.push(mention.slugId)
	}
	return {
		issues: uniqueSorted(issues),
		projects: uniqueSorted(projects),
		documents: uniqueSorted(documents),
	}
}

export const hasLinearMentionLookup = (lookup: LinearMentionLookup) =>
	lookup.issues.length + lookup.projects.length + lookup.documents.length > 0
